<script setup lang="ts">
import { Mask } from 'maska'
import { imService } from '~/lib/services/im/im.service'
import { FiscalNoteFields, IMFiscalData, KeyFiscalData } from '~/lib/services/store/auth.dto'
import { dynamicFiscalKey } from '~/lib/services/store/country/country.dtos'

const props = defineProps<{
  fiscal: IMFiscalData
  isMandatory: 'MANDATORY' | 'MANDATORY_ALWAYS' | 'OPTIONAL'
  formattedMinAmount: string
}>()

const emit = defineEmits<{
  (e: 'set-fiscal', payload: IMFiscalData | undefined): void
  (e: 'set-bill', payload: Record<string, string> | undefined): void
}>()

const authStore = useAuthStore()
const country = useCountry()
const { t } = useLocale()
const { trackOnCloseModal, trackGetBill } = useGTM()

const dynamicFiscalFields = inject(dynamicFiscalKey)!
const previousFiscalData = ref<IMFiscalData>()
const fetching = ref(true)
const openModal = ref(false)

const subtitle = computed(() => {
  if (props.isMandatory === 'OPTIONAL') return t('checkout.sections.fiscal.unfilled.subtitle')
  return props.isMandatory === 'MANDATORY_ALWAYS'
    ? t('checkout.sections.fiscal.unfilled.subtitleMandatoryAlways')
    : t('checkout.sections.fiscal.unfilled.subtitleMandatory', {
        price: props.formattedMinAmount,
      })
})

const setFiscal = (fields: IMFiscalData | undefined) => {
  emit('set-fiscal', fields)
  if (fields?._id) {
    previousFiscalData.value = fields
  }
}

const setBill = (bill: Record<string, any> | undefined) => {
  if (!bill) return emit('set-bill', bill)
  const docTypeVal = bill[FiscalNoteFields.DOCUMENT_TYPE]
  if (docTypeVal) {
    // docType
    const docTypeField = dynamicFiscalFields.find(
      ({ code }) => code === FiscalNoteFields.DOCUMENT_TYPE
    )
    if (!docTypeField) return emit('set-bill', bill)
    const typeOption = docTypeField.options.find(opt => opt.integrationImId === docTypeVal)
    bill[FiscalNoteFields.DOCUMENT_TYPE] = typeOption?.integrationFlexId || docTypeVal
  }
  if ('checkboxSave' in bill) delete bill.checkboxSave
  emit('set-bill', bill)
}

const cleanFiscal = () => {
  setFiscal(undefined)
  setBill(undefined)
}

const openFiscalModal = (behaviour = { sendEvent: false }) => {
  if (behaviour.sendEvent) {
    // track get_bill
    trackGetBill()
  }
  openModal.value = true
}

const closeFiscalModal = () => {
  openModal.value = false
  trackOnCloseModal()
}

const properlyFiscalValue = (key: string, val: string) => {
  if (key !== KeyFiscalData.documentType && key !== KeyFiscalData.documentNumber) return val
  // docType
  const docTypeField = dynamicFiscalFields.find(
    ({ code }) => code === FiscalNoteFields.DOCUMENT_TYPE
  )
  if (!docTypeField) return val
  const typeOption = docTypeField.options.find(
    opt => opt.integrationImId === props.fiscal[KeyFiscalData.documentType]
  )
  if (key === KeyFiscalData.documentNumber) {
    const mask = new Mask({ mask: typeOption?.mask, tokens: JSON.parse(dataMaskaTokens) })
    const masked = mask.masked(val)
    return masked
  }
  return typeOption?.name || val
}

onMounted(async () => {
  try {
    const token = authStore.token!
    const fiscalFieldsList = await imService.fetchFiscalFields({ accessToken: token, country })
    if (fiscalFieldsList.length) {
      previousFiscalData.value = fiscalFieldsList[fiscalFieldsList.length - 1]
    }
  } catch (error: any) {
    //
  } finally {
    fetching.value = false
  }
})
</script>

<template>
  <section data-test="fiscal-fields" class="mb-4 md:mb-0">
    <div class="mb-2 flex flex-row items-center justify-between md:mb-4">
      <header class="text-base font-bold md:text-lg">
        {{ t('checkout.sections.fiscal.title') }}
      </header>

      <!-- Remove fiscal (only when filled and is optional) -->
      <div
        v-if="fiscal && isMandatory === 'OPTIONAL'"
        class="flex cursor-pointer flex-row items-center gap-2 text-2xs text-mcd-secondaryBlue"
        @click="cleanFiscal"
      >
        <p>{{ t('checkout.sections.fiscal.filled.delete') }}</p>
        <img
          class="h-auto w-4 cursor-pointer"
          src="/images/icons/trash_blue.svg"
          alt="Clear fiscal fields"
        />
      </div>
    </div>

    <!-- Fiscal content unfilled -->
    <div v-if="!fiscal" class="flex items-center justify-between gap-4 md:justify-start">
      <p class="text-xs">
        {{ subtitle }}
      </p>

      <McdButton
        :disabled="fetching"
        class="h-6 px-4 py-0 text-2xs"
        @on-click="openFiscalModal({ sendEvent: true })"
      >
        {{ t('checkout.sections.fiscal.unfilled.action') }}
      </McdButton>
    </div>

    <!-- Fiscal content filled  -->
    <div v-else class="flex flex-col gap-4">
      <div class="text-sm">
        <template v-for="[key, field] of Object.entries(fiscal)" :key="key">
          <p v-if="!['_id', 'country'].includes(key)">
            {{ properlyFiscalValue(key, field) }}
          </p>
        </template>
      </div>

      <McdButton
        :disabled="fetching"
        class="h-6 w-fit px-4 py-0 text-2xs"
        @on-click="openFiscalModal()"
      >
        {{ t('checkout.sections.fiscal.filled.action') }}
      </McdButton>
    </div>

    <!-- Lazy fiscal modal -->
    <LazyFiscalDataModal
      v-if="openModal"
      :token="authStore.token"
      :previous-fiscal="previousFiscalData"
      :current-fiscal="fiscal"
      @set-fiscal="setFiscal"
      @set-bill="setBill"
      @close="closeFiscalModal"
    />
  </section>
</template>
